import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, ArrowUpRight, MessageCircle } from "lucide-react";
import { Reveal, LineReveal } from "@/components/Reveal";
import { CONTACT, IMAGES } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [mode, setMode] = useState("contact");

  return (
    <div data-testid="contact-page" className="bg-[color:var(--cream)]">
      <section className="pt-40 lg:pt-48 pb-16 px-6 lg:px-10">
        <div className="mx-auto max-w-[1400px] grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="eyebrow mb-6">— Kontakt</div>
            <LineReveal
              className="display-xl text-6xl md:text-7xl lg:text-[7.5vw] leading-[0.92]"
              lines={["Wir sprechen", "über Ihr Fahrzeug —"]}
            />
            <LineReveal className="display-xl italic text-6xl md:text-7xl lg:text-[7.5vw] leading-[0.92]" lines={["gerne persönlich."]} delay={0.3} />
          </div>
          <Reveal className="lg:col-span-4" delay={0.4}>
            <p className="text-[color:var(--ink-2)] leading-relaxed">
              Der schnellste Weg zu einem Gutachten ist ein kurzes Gespräch. Rufen Sie an, schreiben Sie eine E-Mail, oder hinterlassen Sie Ihre Rückrufwunschzeit.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 px-6 lg:px-10">
        <div className="mx-auto max-w-[1400px] grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Expert card */}
          <Reveal className="lg:col-span-5">
            <div className="border border-black/10 bg-[color:var(--cream-2)] p-8 h-full">
              <div className="aspect-[4/5] overflow-hidden bg-black">
                <img src={IMAGES.work3} alt="Björn Linkowitz" className="w-full h-full object-cover" />
              </div>
              <div className="mt-6">
                <div className="text-[10px] tracking-[0.24em] uppercase text-[color:var(--burgundy)]">Ihr Ansprechpartner</div>
                <div className="font-serif text-3xl mt-2">Björn Linkowitz</div>
                <div className="text-sm text-[color:var(--ink-2)] italic mt-1">Inhaber · Sachverständiger für historische Fahrzeuge</div>
                <p className="mt-5 text-sm text-[color:var(--ink-2)] leading-relaxed">
                  {`„Klassiker sind mehr als Fahrzeuge — sie tragen Geschichten. Meine Aufgabe ist es, diesen Geschichten einen fairen Wert zu geben."`}
                </p>
                <div className="mt-6 space-y-3 text-sm">
                  <a href={`tel:${CONTACT.phoneRaw}`} className="flex items-center gap-3"><Phone className="h-4 w-4 text-[color:var(--burgundy)]" /><span className="link-underline">{CONTACT.phone}</span></a>
                  <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3"><Mail className="h-4 w-4 text-[color:var(--burgundy)]" /><span className="link-underline">{CONTACT.email}</span></a>
                  <div className="flex items-center gap-3"><Clock className="h-4 w-4 text-[color:var(--burgundy)]" /><span>{CONTACT.hoursShort} · <span className="italic">Antwort meist &lt; 24 Std.</span></span></div>
                  <div className="flex items-start gap-3"><MapPin className="h-4 w-4 text-[color:var(--burgundy)] mt-[3px]" /><span>{CONTACT.street}, {CONTACT.city}</span></div>
                </div>
                <a href={`https://wa.me/${CONTACT.whatsapp.replace(/\+/g, "")}`} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 bg-[#25D366] text-white px-5 py-3 text-xs tracking-[0.14em] uppercase font-medium hover:bg-[#1ebe57] transition-colors" data-testid="contact-whatsapp"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
              </div>
            </div>
          </Reveal>

          {/* Forms */}
          <Reveal className="lg:col-span-7" delay={0.15}>
            <div className="flex gap-2 mb-6 border-b border-black/10">
              <button
                data-testid="tab-contact"
                onClick={() => setMode("contact")}
                className={`px-5 py-3 text-xs tracking-[0.14em] uppercase transition-colors ${mode === "contact" ? "text-[color:var(--burgundy)] border-b-2 border-[color:var(--burgundy)]" : "text-[color:var(--ink-2)]"}`}
              >Anfrage senden</button>
              <button
                data-testid="tab-callback"
                onClick={() => setMode("callback")}
                className={`px-5 py-3 text-xs tracking-[0.14em] uppercase transition-colors ${mode === "callback" ? "text-[color:var(--burgundy)] border-b-2 border-[color:var(--burgundy)]" : "text-[color:var(--ink-2)]"}`}
              >Rückruf bestellen</button>
            </div>
            {mode === "contact" ? <ContactForm /> : <CallbackForm />}
          </Reveal>
        </div>
      </section>

      <section className="px-6 lg:px-10 pb-24">
        <div className="mx-auto max-w-[1400px]">
          <div className="eyebrow mb-6">— Standorte</div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="border border-black/10 bg-[color:var(--paper)] p-8">
              <div className="text-[10px] tracking-[0.24em] uppercase text-[color:var(--burgundy)]">Hauptsitz · Hoppegarten</div>
              <div className="font-serif text-2xl mt-2">Kfz-Sachverständigenbüro Björn Linkowitz</div>
              <div className="mt-4 text-[15px] leading-[1.75] text-[color:var(--ink)]">
                Zimmermannsgasse 3<br />15366 Hoppegarten<br />
                Tel.: <a className="link-underline" href={`tel:${CONTACT.phoneRaw}`}>{CONTACT.phone}</a>
              </div>
              <div className="mt-6 border border-black/10 overflow-hidden">
                <iframe
                  title="Karte Hoppegarten"
                  src={`https://www.google.com/maps?q=${CONTACT.mapsQuery}&output=embed`}
                  className="w-full h-[280px] grayscale"
                  loading="lazy"
                  data-testid="contact-map"
                />
              </div>
            </div>
            <div className="border border-black/10 bg-[color:var(--paper)] p-8">
              <div className="text-[10px] tracking-[0.24em] uppercase text-[color:var(--burgundy)]">Nach Vereinbarung · Berlin</div>
              <div className="font-serif text-2xl mt-2">Classic Data Büro in der Classic Remise</div>
              <p className="mt-3 text-[15px] text-[color:var(--ink-2)] leading-relaxed">
                Nach Vereinbarung nutzen wir für persönliche Termine mit Klassiker-Enthusiasten den Standort inmitten der Classic Remise Berlin.
              </p>
              <div className="mt-4 text-[15px] leading-[1.75] text-[color:var(--ink)]">
                Wiebestr. 36 – 37<br />10553 Berlin<br />
                Tel.: <a className="link-underline" href={`tel:${CONTACT.phoneRaw}`}>{CONTACT.phone}</a>
              </div>
              <div className="mt-6 border border-black/10 overflow-hidden">
                <iframe
                  title="Karte Classic Remise Berlin"
                  src={`https://www.google.com/maps?q=Classic+Remise+Wiebestr+36+10553+Berlin&output=embed`}
                  className="w-full h-[280px] grayscale"
                  loading="lazy"
                  data-testid="contact-map-remise"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Danke — wir melden uns in Kürze bei Ihnen.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="contact-form">
      <Field name="name" label="Name" value={form.name} onChange={onChange} required />
      <Field name="email" label="E-Mail" type="email" value={form.email} onChange={onChange} required />
      <Field name="phone" label="Telefon (optional)" value={form.phone} onChange={onChange} />
      <Field name="subject" label="Betreff" value={form.subject} onChange={onChange} />
      <div className="md:col-span-2">
        <label className="text-[10px] tracking-[0.24em] uppercase text-[color:var(--ink-2)]">Nachricht</label>
        <textarea
          name="message" required value={form.message} onChange={onChange} rows={5}
          data-testid="contact-message"
          className="mt-2 w-full bg-transparent border-b border-black/30 focus:border-[color:var(--burgundy)] outline-none py-3 text-[15px] resize-none"
          placeholder="Erzählen Sie uns kurz von Ihrem Fahrzeug…"
        />
      </div>
      <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-4 pt-2">
        <div className="text-xs text-[color:var(--ink-2)]/80">Ihre Daten werden vertraulich behandelt.</div>
        <button disabled={loading} className="btn-primary" data-testid="contact-submit">
          {loading ? "Wird gesendet…" : "Anfrage senden"} <ArrowUpRight className="h-4 w-4" />
        </button>
      </div>
    </form>
  );
}

function CallbackForm() {
  const [form, setForm] = useState({ name: "", phone: "", preferred_time: "", vehicle: "", note: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/callback`, form);
      toast.success("Danke — wir rufen Sie zurück.");
      setForm({ name: "", phone: "", preferred_time: "", vehicle: "", note: "" });
    } catch {
      toast.error("Rückruf konnte nicht angefragt werden. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="callback-form">
      <Field name="name" label="Name" value={form.name} onChange={onChange} required />
      <Field name="phone" label="Telefonnummer" value={form.phone} onChange={onChange} required />
      <Field name="preferred_time" label="Wunschzeit für Rückruf" value={form.preferred_time} onChange={onChange} placeholder="z. B. Mo. Vormittag" />
      <Field name="vehicle" label="Fahrzeug (optional)" value={form.vehicle} onChange={onChange} placeholder="z. B. Mercedes 280 SE, Bj. 1969" />
      <div className="md:col-span-2">
        <label className="text-[10px] tracking-[0.24em] uppercase text-[color:var(--ink-2)]">Kurze Notiz (optional)</label>
        <textarea
          name="note" value={form.note} onChange={onChange} rows={3}
          data-testid="callback-note"
          className="mt-2 w-full bg-transparent border-b border-black/30 focus:border-[color:var(--burgundy)] outline-none py-3 text-[15px] resize-none"
        />
      </div>
      <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-4 pt-2">
        <div className="text-xs text-[color:var(--ink-2)]/80">Wir melden uns innerhalb der Bürozeiten.</div>
        <button disabled={loading} className="btn-primary" data-testid="callback-submit">
          {loading ? "Wird gesendet…" : "Rückruf anfordern"} <ArrowUpRight className="h-4 w-4" />
        </button>
      </div>
    </form>
  );
}

function Field({ label, name, type = "text", value, onChange, required, placeholder }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.24em] uppercase text-[color:var(--ink-2)]">{label}{required && " *"}</span>
      <input
        name={name} type={type} value={value} onChange={onChange} required={required} placeholder={placeholder}
        data-testid={`field-${name}`}
        className="mt-2 w-full bg-transparent border-b border-black/30 focus:border-[color:var(--burgundy)] outline-none py-3 text-[15px]"
      />
    </label>
  );
}
